import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { loadTripBySlug } from '../utils/contentLoader';

export default function TripDetail() {
  const { slug } = useParams();
  const trip = useMemo(() => loadTripBySlug(slug), [slug]);

  if (!trip) {
    return (
      <div style={{ padding: '48px', maxWidth: '760px' }}>
        <Link to="/travel" style={{ color: 'var(--gold)', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', textDecoration: 'none' }}>
          ← Back to Travel
        </Link>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: 'var(--text)', marginTop: '24px' }}>
          Trip not found
        </h1>
        <p style={{ color: 'var(--text-3)' }}>The trip "{slug}" could not be found.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '48px', maxWidth: '820px' }}>

      {/* Back link */}
      <Link
        to="/travel"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--text-3)',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.06em',
          textDecoration: 'none',
          marginBottom: '32px',
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}
      >
        ← TRAVEL
      </Link>

      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.08em',
            color: 'var(--text-3)',
            textTransform: 'uppercase',
          }}>
            {trip.continent}
          </span>
          {trip.year && (
            <>
              <span style={{ color: 'var(--border-2)', fontSize: '0.7rem' }}>·</span>
              <span style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.6rem',
                color: 'var(--text-3)',
              }}>
                {trip.year === 'ongoing' ? 'Ongoing' : trip.year}
              </span>
            </>
          )}
        </div>

        <h1 style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 700,
          letterSpacing: '-0.03em',
          lineHeight: 0.95,
          color: 'var(--text)',
          margin: '0 0 16px',
        }}>
          {trip.title || trip.country}
        </h1>

        {trip.description && (
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: '1.2rem',
            color: 'var(--text-2)',
            margin: 0,
            lineHeight: 1.5,
          }}>
            {trip.description}
          </p>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'var(--border)', marginBottom: '40px' }} />

      {/* Prose content from markdown */}
      <div
        className="prose-detail"
        dangerouslySetInnerHTML={{ __html: trip.contentHtml }}
      />

      {/* Related links */}
      {trip.relatedLinks && trip.relatedLinks.length > 0 && (
        <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
          <div className="eyebrow" style={{ marginBottom: '14px' }}>Related</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {trip.relatedLinks.filter((link) => link && link.href).map((link) => {
              const isExternal = link.href.startsWith('http');
              const sharedStyle = {
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                fontSize: '0.78rem',
                color: 'var(--gold)',
                textDecoration: 'none',
                padding: '6px 12px',
                border: '1px solid transparent',
                borderRadius: '4px',
                background: 'var(--gold-dim)',
                transition: 'border-color 0.15s ease',
                display: 'inline-block',
              };
              if (isExternal) {
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={sharedStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                  >
                    {link.label} ↗
                  </a>
                );
              }
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  style={sharedStyle}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  {link.label} →
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom back link */}
      <div style={{ marginTop: '56px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
        <Link
          to="/travel"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-3)',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.06em',
            textDecoration: 'none',
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}
        >
          ← TRAVEL
        </Link>
      </div>
    </div>
  );
}
